import { loadTickets } from '@features/siteStatusV2/reducer/ticketSlice';
import theme from '@theme';
import React, { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { useAppDispatch } from '../../../../app/hooks';
import { RootState } from '../../../../app/store';
import { Icon } from '../icon';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

interface AssociatedTicketsProps {
  ids: string[];
}

const AssociatedTickets: React.FC<AssociatedTicketsProps> = ({ ids }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const dispatch = useAppDispatch();
  const { error, isTicketsLoading, tickets } = useSelector((state: RootState) => state.ticket);
  useEffect(() => {
    dispatch(loadTickets({ ids }));
  }, [ids]);

  if (isTicketsLoading) {
    return (
      <AssociatedTicketsContainer>
        <AssociatedTicketCard>
          <TicketInformationSkeleton />
        </AssociatedTicketCard>
        <AssociatedTicketCard>
          <TicketInformationSkeleton />
        </AssociatedTicketCard>
      </AssociatedTicketsContainer>
    );
  }

  if (error) {
    return (
      <StateContainer>
        <CustomStateIcon iconName="error-state" />
        <StateMessage>{t('report.associatedTickets.errorFetch')}</StateMessage>
      </StateContainer>
    );
  }

  if (tickets?.length === 0 || tickets === undefined) {
    return (
      <StateContainer>
        <CustomStateIcon iconName="empty-data" />
        <StateMessage>{t('report.associatedTickets.emptyData')}</StateMessage>
      </StateContainer>
    );
  }

  if (tickets && tickets.length > 0) {
    return (
      <AssociatedTicketsContainer>
        {tickets.map((ticket) => (
          <AssociatedTicketCard key={ticket.id}>
            <BoldDot />
            <TicketInformation>
              <HighLightId>{`${ticket.id}: `}</HighLightId>
              {`${ticket.subject} [${ticket.queue}] [${ticket.status}]`}
            </TicketInformation>
          </AssociatedTicketCard>
        ))}
      </AssociatedTicketsContainer>
    );
  }
  return null;
};

const AssociatedTicketsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 0.5rem;
`;

const AssociatedTicketCard = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  border-radius: 0.25rem;
  border-width: 1px;
  padding: 0.25rem;
  border-style: solid;
  border-color: ${theme.palette.grey[100]};
`;

const TicketInformationSkeleton = styled(Skeleton)`
  width: 21rem;
  height: 1rem;
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.palette.grey[200]};
`;

const TicketInformation = styled.div`
  font-weight: 500;
  font-size: 0.563rem;
  line-height: 0.75rem;
  color: ${theme.palette.grey[400]};
`;

const BoldDot = styled.div`
  background-color: ${theme.palette.grey[700]};
  border-radius: 50%;
  margin-top: 0.25rem;
  padding: 0.15rem;
`;

const HighLightId = styled.span`
  font-weight: 700;
  color: ${theme.palette.grey[900]};
`;

const StateContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const CustomStateIcon = styled(Icon)`
  height: 5rem;
`;

const StateMessage = styled.div`
  font-weight: 500;
  font-size: 0.563rem;
  line-height: 0.75rem;
  color: ${theme.palette.grey[400]};
`;

export default AssociatedTickets;
